import {
  InternalChannelStats,
  InternalOpsChannel,
  InternalOpsChannelMemberUser,
  useInternalChannelStats,
} from "@/lib/api/internal-ops-messaging";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import {
  CalendarIcon,
  ChevronDoubleRightIcon,
  CircleStackIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { cn, getInitials } from "@/lib/utils";
import { useBatchSignedMedia } from "@/lib/hooks/use-signed-media";
import { useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { DateTime } from "luxon";
import { ComponentType, SVGProps, useMemo, useRef, useState } from "react";
import { RenameableGroupName } from "./renameable-group-name";

type ChannelMember = NonNullable<InternalOpsChannel["members"]>[number];

const MAX_RENDERED_MEMBERS = 100;
const MEMBER_ROW_HEIGHT = 56;

interface ChatDetailsPanelProps {
  internalChannel: InternalOpsChannel | null;
  appearance?: "legacy" | "workflow";
}

export function ChatDetailsPanel({
  internalChannel,
  appearance = "legacy",
}: ChatDetailsPanelProps) {
  const sb = useSendbirdStateContext();
  const currentUserId = sb.config.userId;
  const { currentGroupChannel } = useChannelContext();

  return (
    <ChatDetailsPanelContent
      internalChannel={internalChannel}
      currentUserId={currentUserId}
      fallbackDisplayName={currentGroupChannel?.name}
      appearance={appearance}
    />
  );
}

interface ChatDetailsPanelContentProps {
  internalChannel: InternalOpsChannel | null;
  currentUserId: string;
  fallbackDisplayName?: string | null;
  appearance?: "legacy" | "workflow";
}

export function ChatDetailsPanelContent({
  internalChannel,
  currentUserId,
  fallbackDisplayName,
  appearance = "legacy",
}: ChatDetailsPanelContentProps) {
  const [collapsed, setCollapsed] = useState(false);

  const otherEmbeddedMember = useMemo(() => {
    if (!internalChannel?.members) return null;
    return (
      internalChannel.members.find(
        (m) => m.user_id !== currentUserId && !!m.user,
      ) ?? null
    );
  }, [internalChannel, currentUserId]);

  const isCohort = internalChannel?.kind === "cohort";
  const isGroup = internalChannel?.kind === "group";
  const showStatsPanel = isCohort || isGroup;

  if (collapsed) {
    return (
      <button
        onClick={() => setCollapsed(false)}
        className={cn(
          "flex w-12 cursor-pointer items-center justify-center border-l bg-white",
          appearance === "workflow" ? "border-surfaces-z4" : "border-zinc-200",
        )}
        aria-label="Expand details"
      >
        <ChevronDoubleRightIcon className="size-5 rotate-180 text-zinc-400" />
      </button>
    );
  }

  return (
    <aside
      aria-label="Support details"
      className={cn(
        "flex w-[360px] shrink-0 flex-col border-l",
        appearance === "workflow"
          ? "border-surfaces-z4 bg-white"
          : "border-zinc-200 bg-[#f9fafb]",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between border-b bg-white px-4",
          appearance === "workflow"
            ? "border-surfaces-z4 py-3"
            : "border-zinc-200 py-4",
        )}
      >
        <h3
          className={cn(
            "font-semibold",
            appearance === "workflow"
              ? "text-base text-text-primary"
              : "text-sm text-black",
          )}
        >
          Details
        </h3>
        <button
          onClick={() => setCollapsed(true)}
          className="text-zinc-400 hover:text-zinc-600"
          aria-label="Collapse details"
        >
          <ChevronDoubleRightIcon className="size-5" />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4">
        {showStatsPanel && internalChannel ? (
          <ChannelStatsDetails
            channelId={internalChannel.id}
            kind={isCohort ? "cohort" : "group"}
            members={internalChannel.members ?? []}
            fallbackDisplayName={
              internalChannel.channel_display_name ?? fallbackDisplayName
            }
          />
        ) : otherEmbeddedMember?.user ? (
          <DirectDetails user={otherEmbeddedMember.user} />
        ) : (
          <div className="text-sm text-zinc-500">No member selected</div>
        )}
      </div>
    </aside>
  );
}

function ChannelStatsDetails({
  channelId,
  kind,
  members,
  fallbackDisplayName,
}: {
  channelId: string;
  kind: "cohort" | "group";
  members: ChannelMember[];
  fallbackDisplayName?: string | null;
}) {
  const { data: stats, isLoading } = useInternalChannelStats(channelId);

  const subtitle = useMemo(() => buildStatsSubtitle(stats ?? null), [stats]);
  const avgTenure = useMemo(
    () => formatTenureMonths(stats?.avgTenureMonths ?? null),
    [stats?.avgTenureMonths],
  );
  const groupsValue = useMemo(() => {
    if (!stats || kind !== "cohort") return null;
    return stats.groups.length === 0 ? "All" : stats.groups.join(", ");
  }, [stats, kind]);

  const resolvedDisplayName =
    stats?.displayName ||
    fallbackDisplayName ||
    (kind === "cohort" ? "Cohort" : "Group");

  return (
    <>
      <div className="flex flex-col gap-1">
        {kind === "group" ? (
          <RenameableGroupName
            channelId={channelId}
            displayName={resolvedDisplayName}
          />
        ) : (
          <h2 className="text-xl font-bold leading-7 text-[#020817]">
            {resolvedDisplayName}
          </h2>
        )}
        {subtitle && (
          <p className="text-base leading-6 text-[#6b7280]">{subtitle}</p>
        )}
      </div>

      <div className="border-t border-[#e2e2e2] pt-4">
        <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_2px_4px_0_rgba(0,0,0,0.05)]">
          {isLoading && !stats ? (
            <div className="space-y-2">
              <div className="h-4 w-2/3 animate-pulse rounded bg-zinc-100" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-zinc-100" />
              <div className="h-4 w-1/3 animate-pulse rounded bg-zinc-100" />
            </div>
          ) : (
            <>
              {avgTenure && (
                <InfoRow
                  icon={CalendarIcon}
                  title="Average time with Somethings"
                  value={avgTenure}
                />
              )}
              {stats && stats.stateCount > 0 && (
                <InfoRow
                  icon={MapPinIcon}
                  title="Locations"
                  value={`${stats.stateCount} ${
                    stats.stateCount === 1 ? "state" : "states"
                  }`}
                />
              )}
              {groupsValue && (
                <InfoRow
                  icon={CircleStackIcon}
                  title="Groups"
                  value={groupsValue}
                />
              )}
            </>
          )}
        </div>
      </div>

      <MemberList members={members} totalCount={stats?.memberCount ?? null} />
    </>
  );
}

function MemberList({
  members,
  totalCount,
}: {
  members: ChannelMember[];
  totalCount: number | null;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const visibleMembers = useMemo(
    () =>
      members
        .filter(
          (
            m,
          ): m is ChannelMember & {
            user: NonNullable<ChannelMember["user"]>;
          } => !!m.user,
        )
        .slice(0, MAX_RENDERED_MEMBERS),
    [members],
  );

  const signedAvatars = useBatchSignedMedia(
    visibleMembers.map((m) => m.user.profile_picture_url ?? null),
  );

  const virtualizer = useVirtualizer({
    count: visibleMembers.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MEMBER_ROW_HEIGHT,
    overscan: 8,
  });

  if (visibleMembers.length === 0) return null;

  const resolvedTotal = totalCount ?? members.length;
  const showingLabel =
    resolvedTotal > visibleMembers.length
      ? `Showing ${visibleMembers.length} of ${resolvedTotal}`
      : `${visibleMembers.length} ${visibleMembers.length === 1 ? "member" : "members"}`;

  return (
    <div className="border-t border-[#e2e2e2] pt-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold text-black">Members</h3>
        <span className="text-xs text-[#6b7280]">{showingLabel}</span>
      </div>
      <div
        ref={scrollRef}
        className="max-h-[360px] overflow-y-auto rounded-xl bg-white shadow-[0px_2px_4px_0_rgba(0,0,0,0.05)]"
      >
        <div
          className="relative w-full"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const member = visibleMembers[virtualRow.index];
            const avatarUrl = signedAvatars[virtualRow.index]?.url;
            return (
              <div
                key={member.user_id}
                className="absolute left-0 top-0 w-full"
                style={{
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <MemberRow user={member.user} avatarUrl={avatarUrl} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function MemberRow({
  user,
  avatarUrl,
}: {
  user: NonNullable<ChannelMember["user"]>;
  avatarUrl?: string;
}) {
  const roleLabel = formatRoleLabel(user.role);
  return (
    <div className="flex h-full items-center gap-3 px-3">
      <Avatar className="size-9 shrink-0">
        <AvatarImage src={avatarUrl} alt={user.name} />
        <AvatarFallback className="bg-zinc-200 text-sm text-zinc-500">
          {getInitials(user.name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-black">
          {user.name}
        </span>
        {roleLabel && (
          <span className="truncate text-xs text-[#6b7280]">{roleLabel}</span>
        )}
      </div>
    </div>
  );
}

function buildStatsSubtitle(stats: InternalChannelStats | null): string | null {
  if (!stats) return null;
  const parts: string[] = [];
  if (stats.memberCount > 0) {
    parts.push(
      `${stats.memberCount} ${stats.memberCount === 1 ? "member" : "members"}`,
    );
  }
  return parts.length > 0 ? parts.join(" • ") : null;
}

function formatTenureMonths(months: number | null): string | null {
  if (months == null) return null;
  if (months < 1) return "Less than a month";
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  if (remMonths > 0)
    parts.push(`${remMonths} ${remMonths === 1 ? "month" : "months"}`);
  return parts.join(", ");
}

function DirectDetails({ user }: { user: InternalOpsChannelMemberUser }) {
  const navigate = useNavigate();
  const subtitleLabel = useMemo(() => formatRoleLabel(user.role), [user.role]);
  const isMentor = isMentorIdentity(user.system_roles);

  const handleViewPerformance = () => {
    navigate({
      to: "/mentor-data-dashboard",
      search: { mentorId: user.id },
    });
  };

  const tenure = useMemo(() => {
    if (!user.created_at) return null;
    const created = DateTime.fromISO(user.created_at);
    if (!created.isValid) return null;
    return formatTenureMonths(
      Math.floor(DateTime.now().diff(created, "months").months),
    );
  }, [user.created_at]);

  const subtitleParts = [subtitleLabel, user.pronouns].filter(
    (part): part is string => !!part && part.length > 0,
  );

  return (
    <>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-bold leading-7 text-[#020817]">
          {user.name}
        </h2>
        {subtitleParts.length > 0 && (
          <p className="flex flex-wrap items-center gap-x-3 text-base leading-6 text-[#6b7280]">
            {subtitleParts.map((part, idx) => (
              <span
                key={`${part}-${idx}`}
                className="inline-flex items-center gap-3"
              >
                {idx > 0 && (
                  <span aria-hidden className="text-[#6b7280]">
                    •
                  </span>
                )}
                <span>{part}</span>
              </span>
            ))}
          </p>
        )}
      </div>

      <div className="border-t border-[#e2e2e2] pt-4">
        <div className="flex flex-col gap-4 rounded-xl bg-white p-4 shadow-[0px_2px_4px_0_rgba(0,0,0,0.05)]">
          {tenure && (
            <InfoRow
              icon={CalendarIcon}
              title="Time with Somethings"
              value={tenure}
            />
          )}
          {user.email && (
            <InfoRow icon={EnvelopeIcon} title="Email" value={user.email} />
          )}
          {user.phone && (
            <InfoRow icon={PhoneIcon} title="Phone" value={user.phone} />
          )}
          {user.location && (
            <InfoRow icon={MapPinIcon} title="Location" value={user.location} />
          )}
        </div>
      </div>

      {isMentor && (
        <Button
          variant="default"
          size="sm"
          onClick={handleViewPerformance}
          className="w-full gap-2 rounded-[10px] bg-[#1C1C1C] hover:bg-[#1C1C1C]/90"
        >
          View Performance
        </Button>
      )}
    </>
  );
}

function InfoRow({
  icon: Icon,
  title,
  value,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="size-5 shrink-0 text-black" />
      <div className="flex min-w-0 flex-1 flex-col text-xs">
        <span className="font-bold text-black">{title}</span>
        <span className="truncate text-[#5c5c5c]">{value}</span>
      </div>
    </div>
  );
}
